import React, { useState, useEffect } from 'react';
import { getAllDeviceStocks } from '../services/api';
import { Package, Search, ChevronLeft, ChevronRight, Calculator, Calendar, Tag } from 'lucide-react';
import Skeleton from '../Components/SkeletonLoader';

const StockListPage = () => {
    const [stocks, setStocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchStocks = async () => {
        setLoading(true);
        try {
            const response = await getAllDeviceStocks(pageNo, pageSize);
            setStocks(response.data || []);
            setTotalCount(response.count || 0);
        } catch (error) {
            console.error("Failed to fetch stocks:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStocks();
    }, [pageNo, pageSize]);

    const totalPages = Math.ceil(totalCount / pageSize);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPageNo(newPage);
        }
    };

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                        <Package className="h-6 w-6 text-blue-600" />
                        Stock Management
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {totalCount} Items
                        </span>
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">View and manage device inventory.</p>
                </div>
            </div>

            {/* Content Card */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                <th className="px-6 py-4 w-20">#</th>
                                <th className="px-6 py-4">Device Details</th>
                                <th className="px-6 py-4">Hardware Identifiers</th>
                                <th className="px-6 py-4 text-right">Added On</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                [1, 2, 3, 4, 5].map((i) => (
                                    <tr key={i}>
                                        <td className="px-6 py-4"><Skeleton className="h-4 w-8" /></td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-2">
                                                <Skeleton className="h-4 w-32" />
                                                <Skeleton className="h-3 w-24" />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-2">
                                                <Skeleton className="h-4 w-32" />
                                                <Skeleton className="h-3 w-40" />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4"><Skeleton className="h-4 w-24 ml-auto" /></td>
                                    </tr>
                                ))
                            ) : stocks.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center gap-2">
                                            <div className="p-3 bg-gray-50 rounded-full">
                                                <Package className="h-6 w-6 text-gray-300" />
                                            </div>
                                            <span className="text-sm font-medium">No stock items found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                stocks.map((item, index) => (
                                    <tr key={item.id} className="group hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-4 text-gray-500 font-mono text-xs">{(pageNo - 1) * pageSize + index + 1}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-semibold text-gray-900 flex items-center gap-2">
                                                    {item.deviceType?.type || 'Unknown Type'}
                                                </span>
                                                <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                                    <Tag className="h-3 w-3" />
                                                    {item.manufacturer?.company || 'Unknown Manufacturer'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className="font-bold text-gray-400 uppercase w-12">Serial</span>
                                                    <span className="font-mono text-gray-700 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                                                        {item.serialNo || '-'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className="font-bold text-gray-400 uppercase w-12">MAC</span>
                                                    <span className="font-mono text-gray-700">
                                                        {item.macAddress || '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2 text-sm text-gray-500">
                                                <Calendar className="h-3.5 w-3.5 text-gray-400" />
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {totalCount > 0 && (
                    <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <span className="text-xs text-gray-500 font-medium">
                            Showing <span className="text-gray-900 font-bold">{stocks.length}</span> of <span className="text-gray-900 font-bold">{totalCount}</span>
                        </span>
                        
                        <div className="flex items-center gap-3">
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPageNo(1);
                                }}
                                className="text-xs border-gray-300 rounded-lg py-1.5 pl-2 pr-6 bg-white focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm"
                            >
                                {[10, 20, 50, 100].map(size => (
                                    <option key={size} value={size}>{size} / page</option>
                                ))}
                            </select>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => handlePageChange(pageNo - 1)}
                                    disabled={pageNo === 1}
                                    className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="px-3 text-xs font-medium text-gray-700">
                                    Page {pageNo} of {totalPages || 1}
                                </span>
                                <button
                                    onClick={() => handlePageChange(pageNo + 1)}
                                    disabled={pageNo >= totalPages}
                                    className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StockListPage;
